import React, { useMemo } from "react";
import { useGetFacilitiesV2Query } from "@/features/api/apiSlice";
import { UnitItem } from "@/components/units/unit/unitItem";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { UnitItemLoading } from "@/components/units/unit/unitItemLoading";
import { NoUnitsAvailable } from "./noUnitsAvailable";
import { UnitError } from "@/components/units/unit/unitError";
import type { UnitGroup } from "@/types";
import { FacilitiesContext } from "@/context/facilitiesContext";
import { UnavailableUnitItem } from "@/components/units/unit/unavailableUnitItem";
import { filterUnitsByFeatureCategory } from "@/utils/units/unitFilters";
import { sortUnitsByProperty } from "@/utils/units/unitSorting";
import ErrorBoundary from "../ErrorBoundary";

type UnitTableProps = {
    facilityIds: number[];
    units: UnitGroup[];
    unitsError: unknown;
    unitsIsLoading: boolean;
    selectedFeatures: number[];
    selectedCategories: number[];
    sort: string;
};

export const UnitTable = ({
    facilityIds,
    units,
    unitsError,
    unitsIsLoading,
    selectedFeatures,
    selectedCategories,
    sort,
}: UnitTableProps) => {
    const displayOptions = useDisplayOptionContext();
    const storedApiKey = useSelector((state: RootState) => state.apiKey.apiKey);

    const facilityParams = { ids: facilityIds.join(","), withSettings: true };

    // Fetch unit groups and facilities using API

    // Extract sorting property and order from the sort string
    const [sortProperty, sortOrder] = sort.split(":") as [
        "vacancy" | "price" | "area",
        "asc" | "desc",
    ];

    const { data: facilitiesData } = useGetFacilitiesV2Query(facilityParams, {
        skip: !storedApiKey,
    });

    const urlParams = new URLSearchParams(window.location.search);
    const unitTypeName = urlParams.get('unit_type_name');

    // Compute filtered and sorted units
    const filteredAndSortedUnits = useMemo(() => {
        const filteredUnits = filterUnitsByFeatureCategory(
            units.filter(
                (unitGroup: UnitGroup) => {
                    if(unitTypeName && unitGroup.representative_unit.unit_type_name !== unitTypeName) {
                        return false;
                    }
                    return displayOptions.unavailableUnits || unitGroup.available > 0;
                }
            ) || [],
            selectedFeatures,
            selectedCategories
        );

        return sortUnitsByProperty(filteredUnits, sortProperty, sortOrder);
    }, [units, selectedFeatures, selectedCategories, displayOptions, sortProperty, sortOrder]);


    // Render UI
    if (unitsError) {
        return (
            <div className="unit-table">
                <UnitError />
                <p>
                    Error: {unitsError.status} -{" "}
                    {JSON.stringify(unitsError.data)}
                </p>
            </div>
        );
    }

    if (unitsIsLoading) {
        return (
            <div className="unit-table">
                {Array.from({ length: 6 }, (_, index) => (
                    <UnitItemLoading
                        layout={displayOptions.tableLayout ?? "row"}
                        key={`loading-item-${index}`}
                    />
                ))}
            </div>
        );
    }

    if (!units || units.length === 0) {
        return (
            <div className="unit-table">
                <UnitError />
            </div>
        );
    }

    if (filteredAndSortedUnits.length === 0) {
        return (
            <div className="unit-table">
                <ErrorBoundary>
                    <NoUnitsAvailable />
                </ErrorBoundary>
            </div>
        );
    }

    const facilitiesContextValue = {
        facilities: facilitiesData?.data || [],
    };

    return (
        <div className="unit-table">
            <FacilitiesContext.Provider value={facilitiesContextValue}>
                {filteredAndSortedUnits.map((unitGroup: UnitGroup) => {
                    return unitGroup.available === 0 &&
                    unitGroup.show_nearby_units ? (
                        <UnavailableUnitItem
                            key={unitGroup.representative_unit.id}
                            unitGroup={unitGroup}
                            layout={displayOptions.tableLayout ?? "row"}
                        />
                    ) : (
                        <UnitItem
                            key={unitGroup.representative_unit.id}
                            unitGroup={unitGroup}
                            layout={displayOptions.tableLayout ?? "row"}
                        />
                    );
                })}
            </FacilitiesContext.Provider>
        </div>
    );
};
